import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { Fetcher } from '../../../Helpers/fetcher';
import { Notify } from '../../../Helpers/notification';
import { Card, Form, Button, InputGroup, Spinner } from 'react-bootstrap';
import { FaShieldAlt, FaSearch, FaArrowLeft } from 'react-icons/fa';
import { normalizePermissions } from '../helpers/normalizePermissions';
import "../UpdateAcl/index.css";
import { buildExpandedModulesFromPermissions, parseAclModuleListingData, sortModules, NUMBER_ACTION_OPTIONS } from '../helpers/aclHelper';
import AclTable from '../components/AclTable';

const ViewAcl = ({ app, id }) => {
    const apiPath = app.apiPath;
    const userId = app.userId;
    const history = useHistory();

    const [aclName, setAclName] = useState('');
    const [moduleList, setModuleList] = useState([]);
    const [permissions, setPermissions] = useState({});
    const [search, setSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState({});
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const [moduleListingData, setModuleListingData] = useState({});
    const [multiSelectOptions, setMultiSelectOptions] = useState({});

    const fetchModuleData = async () => {
        try {
            const dialerUrl = `${apiPath}/api/obd/dialers`;
            const moduleUrl = `${apiPath}/api/obd/module`;
            const locationUrl = `${apiPath}/api/obd/locations/${userId}`;

            const transform = (arr, label, value) =>
                (Array.isArray(arr) ? arr : []).map(item => ({
                    name: item?.[label] ?? "",
                    id: item?.[value] ?? "",
                    dialerName: item?.dialerName ?? ""
                }));

            const [dialerRes, moduleRes, locationRes] = await Promise.all([
                Fetcher.get(dialerUrl),
                Fetcher.get(moduleUrl),
                Fetcher.get(locationUrl).catch(() => [])
            ]);

            setMultiSelectOptions({
                "Manage Dialers": transform(dialerRes, "dialerName", "dialerId"),
                "Manage Users": transform(moduleRes, "moduleName", "moduleId"),
                "Manage Locations": transform(locationRes, "locationName", "locationId"),
                "Number Action": NUMBER_ACTION_OPTIONS
            });

        } catch (err) {
            console.log("fetchModuleData error", err);
        }
    };

    const getModuleList = async () => {
        setLoading(true);
        try {
            const res = await Fetcher.get(`${apiPath}/api/obd/acl/module/list`);

            const data = (Array.isArray(res) ? res : []).map(m => {
                let children = [];
                try {
                    children = Array.isArray(m.childModule)
                        ? m.childModule
                        : JSON.parse(m.childModule || "[]");
                } catch {
                    children = [];
                }

                return { ...m, children };
            });

            setModuleList(data);

        } catch {
            Notify.notify("Error loading modules");
        } finally {
            setLoading(false);
        }
    };

    const getAclDetail = async () => {
        setPageLoading(true);
        try {
            const res = await Fetcher.get(
                `${apiPath}/api/obd/get/acl/setting/${id}`
            );

            setAclName(res?.aclName || '');

            const data = res?.moduleOperation
                ? (typeof res.moduleOperation === "string"
                    ? JSON.parse(res.moduleOperation)
                    : res.moduleOperation)
                : {};

            const normalized = normalizePermissions(data);
            setPermissions(normalized);

            const expanded = buildExpandedModulesFromPermissions(data);
            setExpandedModules(expanded);

            const restored = parseAclModuleListingData(res?.moduleListingData);
            setModuleListingData(restored);
        } catch {
            Notify.notify("Failed to load ACL details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            await getModuleList();
            await fetchModuleData();
            if (id) {
                await getAclDetail();
            }
        };
        load();
    }, [id]);

    const noop = () => {};

    const isAllChecked = (moduleName, keys) => {
        const p = permissions[moduleName] || {};
        return keys.every(k => p[k] === true);
    };

    const toggleExpand = (moduleName) => {
        setExpandedModules(prev => ({
            ...prev,
            [moduleName]: !prev[moduleName]
        }));
    };

    const filteredModules = moduleList.sort(sortModules).filter(m =>
        m.mname.toLowerCase().includes(search.toLowerCase())
    );

    if (pageLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <div>Loading ACL...</div>
            </div>
        );
    }

    return (
        <Card className="acl-card border-0 shadow-sm">
            <Card.Header className="acl-card-header">
                <div className="acl-header-icon">
                    <FaShieldAlt size={18} />
                </div>
                <div>
                    <h5 className="acl-header-title">View ACL</h5>
                    <p className="acl-header-subtitle">Read-only view of the modules and permissions granted to this ACL</p>
                </div>
            </Card.Header>
            <Card.Body className="acl-card-body">

                <div className="acl-section">
                    <div className="acl-section-title">Basic Details</div>
                    <Form.Group className="mb-0">
                        <Form.Label>ACL Name</Form.Label>
                        <Form.Control value={aclName} readOnly disabled />
                    </Form.Group>
                </div>

                <div className="acl-section">
                    <div className="acl-section-title">Module Permissions</div>

                    <InputGroup className="acl-search mb-3">
                        <InputGroup.Text className="acl-search-icon">
                            <FaSearch />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Search module..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </InputGroup>

                    <div className="acl-table-wrapper">
                        <AclTable
                            loading={loading}
                            filteredModules={filteredModules}
                            permissions={permissions}
                            expandedModules={expandedModules}
                            moduleListingData={moduleListingData}
                            multiSelectOptions={multiSelectOptions}
                            togglePermission={noop}
                            toggleAll={noop}
                            isAllChecked={isAllChecked}
                            toggleExpand={toggleExpand}
                            setModuleListingData={noop}
                            readOnly
                        />
                    </div>
                </div>

            </Card.Body>

            <Card.Footer className="acl-card-footer">
                <Button variant="secondary" onClick={() => history.push("/acl-settings")} className="acl-save-btn">
                    <FaArrowLeft />
                    Back to ACL List
                </Button>
            </Card.Footer>
        </Card>
    );
};

export default ViewAcl;
